import type { Side } from "./api";

/** "CADC/USDC" → its base and quote symbols. */
export function splitPair(pair: string): { base: string; quote: string } {
  const [base = pair, quote = ""] = pair.split("/");
  return { base, quote };
}

/** The column heads of a per-direction table: the pair's base when the network quotes one pair, else generic words. */
export function sideHeads(pairs: readonly string[]): { bid: string; ask: string } {
  const bases = [...new Set(pairs.map((p) => splitPair(p).base))];
  const base = bases.length === 1 ? bases[0]! : "the base coin";
  return { bid: `Customers sell ${base}`, ask: `Customers buy ${base}` };
}

/**
 * "Customers sell BRZ for USDT" (bid) or "Customers buy BRZ with USDT" (ask):
 * both coins, so BRZ/USDT and BRZ/USDC never read alike; the base alone
 * ("Customers sell BRZ") where `both` is false, on a network of one pair.
 */
export function directionName(pair: string, side: Side, both = true): string {
  const { base, quote } = splitPair(pair);
  if (!both) return side === "bid" ? `Customers sell ${base}` : `Customers buy ${base}`;
  return side === "bid" ? `Customers sell ${base} for ${quote}` : `Customers buy ${base} with ${quote}`;
}

/** "customers selling BRZ for USDT", "customers buying BRZ with USDT"; the base alone where `both` is false. */
export function directionGerund(pair: string, side: Side, both = true): string {
  const { base, quote } = splitPair(pair);
  const verb = side === "bid" ? "selling" : "buying";
  return both ? `customers ${verb} ${base} ${side === "bid" ? "for" : "with"} ${quote}` : `customers ${verb} ${base}`;
}

/**
 * The directions in which a coin is paid, joined: "customers buy BRZ with USDT
 * or with USDC". `form` picks "customers sell …" (plain) or
 * "customers selling …" (gerund).
 */
export function directionsText(directions: readonly { pair: string; side: Side }[], both: boolean, form: "plain" | "gerund" = "plain"): string {
  const groups: { head: string; tails: string[] }[] = [];
  for (const d of directions) {
    const { base, quote } = splitPair(d.pair);
    const verb = form === "plain" ? (d.side === "bid" ? "sell" : "buy") : d.side === "bid" ? "selling" : "buying";
    const head = `customers ${verb} ${base}`;
    const group = groups.find((g) => g.head === head) ?? groups[groups.push({ head, tails: [] }) - 1]!;
    const tail = `${d.side === "bid" ? "for" : "with"} ${quote}`;
    if (both && !group.tails.includes(tail)) group.tails.push(tail);
  }
  return groups.map((g) => (g.tails.length === 0 ? g.head : `${g.head} ${g.tails.join(" or ")}`)).join(" or ");
}
